import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { ensureRadarAnim } from "../ClubRadarBits";
import { GLYPH } from "./symbols";

// =====================================================================
// Club Radar V2 — overlay primitives (DESIGN-SPEC §4 items 13/14/15,
// §3 GRAB/DANGER-ICON). Purely presentational: open/close state and all
// actions are wired by the callers (existing handlers only).
//
// Portal idiom mirrors the sibling kits (agency/v2/overlays.js:
// createPortal(document.body), z-[100]). Animations reuse the shared
// `tp-pop`/`tp-sheet` keyframes via ensureRadarAnim (ClubRadarBits) —
// one <style id="tp-radar-anim"> serves agent + club radar (quirk #10).
// =====================================================================

// ── Desktop modal shell — §4 item 14 ─────────────────────────────────
// Scrim `rgba(15,23,42,.40)`; panel w430–560, r16, shadow
// `0 28px 60px rgba(15,23,42,.30)`, pad ~20–22.
export const ModalShell = ({ open, onClose, width = 560, padding = "20px 22px", style, children }) => {
  useEffect(() => {
    if (open) ensureRadarAnim();
  }, [open]);
  if (!open) return null;
  return createPortal(
    <div
      data-testid="cr-modal-scrim"
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{ background: "rgba(15,23,42,.40)", padding: 24 }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-h-full overflow-y-auto"
        style={{
          maxWidth: width,
          background: "#fff",
          borderRadius: 16,
          boxShadow: "0 28px 60px rgba(15,23,42,.30)",
          padding,
          animation: "tp-pop .18s ease",
          ...style,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body
  );
};

// Right-aligned modal footer (Cancel + primary, gap 8, mt 16–18).
export const ModalFooter = ({ marginTop = 16, style, children }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      gap: 8,
      marginTop,
      ...style,
    }}
  >
    {children}
  </div>
);

// ── GRAB — bottom-sheet grab handle (1m/1o/1q/2b) ────────────────────
// 38×4, r2, `#E5E7EB`, centered.
export const GrabHandle = ({ marginBottom = 12, style, ...rest }) => (
  <div
    data-testid="cr-grab-handle"
    style={{ display: "flex", justifyContent: "center", marginBottom, ...style }}
    {...rest}
  >
    <div style={{ width: 38, height: 4, borderRadius: 2, background: "#E5E7EB" }} />
  </div>
);

// ── Bottom sheet shell — §4 item 15 ──────────────────────────────────
// Scrim `.44`, panel anchored bottom, r `20 20 0 0`, shadow
// `0 -12px 40px rgba(15,23,42,.22)`, pad `10px 16px 18px`, grab handle.
export const BottomSheetShell = ({ open, onClose, padding = "10px 16px 18px", withHandle = true, style, children }) => {
  useEffect(() => {
    if (open) ensureRadarAnim();
  }, [open]);
  if (!open) return null;
  return createPortal(
    <div
      data-testid="cr-sheet-scrim"
      className="fixed inset-0 z-[100] flex flex-col justify-end"
      style={{ background: "rgba(15,23,42,.44)" }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-h-[92vh] overflow-y-auto"
        style={{
          background: "#fff",
          borderRadius: "20px 20px 0 0",
          boxShadow: "0 -12px 40px rgba(15,23,42,.22)",
          padding,
          animation: "tp-sheet .22s ease",
          ...style,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {withHandle && <GrabHandle />}
        {children}
      </div>
    </div>,
    document.body
  );
};

// ── ⋯ menu popover — §4 item 13 (identical in 1b and 2d) ─────────────
// w224, bg `#fff`, border `#E5E7EB`, r12, shadow menu, pad 6. Position
// comes from the caller (absolute within its relative header). Items
// are children (MenuItem/MenuDivider) — the creator vs colleague set is
// role-FILTERED by the caller from existing `can_manage` logic.
export const MenuPopover = ({ width = 224, shadow = "0 14px 34px rgba(15,23,42,.16)", style, children, ...rest }) => (
  <div
    role="menu"
    style={{
      width,
      background: "#fff",
      border: "1px solid #E5E7EB",
      borderRadius: 12,
      boxShadow: shadow,
      padding: 6,
      zIndex: 5,
      ...style,
    }}
    {...rest}
  >
    {children}
  </div>
);

// Menu item: pad `8px 10px`, r8, 13px/600, `#111827`; destructive
// `#DC2626`; hover bg `#F8FAFC`.
export const MenuItem = ({ destructive = false, style, children, ...rest }) => (
  <button
    type="button"
    role="menuitem"
    className="hover:bg-[#F8FAFC]"
    style={{
      display: "block",
      width: "100%",
      textAlign: "left",
      padding: "8px 10px",
      borderRadius: 8,
      border: "none",
      background: "transparent",
      color: destructive ? "#DC2626" : "#111827",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      fontFamily: "inherit",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      ...style,
    }}
    {...rest}
  >
    {children}
  </button>
);

// Menu divider: h1 `#F1F5F9`, margin `5px 4px`.
export const MenuDivider = () => (
  <div role="separator" style={{ height: 1, background: "#F1F5F9", margin: "5px 4px" }} />
);

// ── DANGER-ICON — confirm warning tile (1p/1q) ───────────────────────
// 40×40 r12 desktop / 38×38 mobile, bg `#FEE2E2`, "!" `#DC2626`
// 19/18px 800.
export const DangerIconTile = ({ size = 40, fontSize = 19, style, ...rest }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: 12,
      background: "#FEE2E2",
      color: "#DC2626",
      fontSize,
      fontWeight: 800,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      ...style,
    }}
    {...rest}
  >
    {GLYPH.bang}
  </div>
);

// ── Confirm dialog shell (1p desktop modal / 1q mobile sheet) ────────
// Copy (title/body) and the action buttons arrive fully composed from
// the caller — real counts/list names, never baked samples.
export const ConfirmDialogShell = ({ open, onClose, mobile = false, title, body, actions }) => {
  if (mobile) {
    return (
      <BottomSheetShell open={open} onClose={onClose}>
        <div style={{ marginTop: 2 }}>
          <DangerIconTile size={38} fontSize={18} />
          <div style={{ marginTop: 10, fontSize: 15.5, fontWeight: 800, color: "#111827" }}>{title}</div>
          <div style={{ marginTop: 5, fontSize: 12.5, color: "#6B7280", lineHeight: 1.55 }}>{body}</div>
          <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>{actions}</div>
        </div>
      </BottomSheetShell>
    );
  }
  return (
    <ModalShell open={open} onClose={onClose} width={430} padding={22}>
      <DangerIconTile />
      <div style={{ marginTop: 12, fontSize: 16, fontWeight: 800, color: "#111827" }}>{title}</div>
      <div style={{ marginTop: 6, fontSize: 13, color: "#6B7280", lineHeight: 1.55 }}>{body}</div>
      <ModalFooter marginTop={18}>{actions}</ModalFooter>
    </ModalShell>
  );
};
